import type { IconSize } from '@components/v2/types';
import Icon from '@components/v2/ui/Icon/Icon';
import { Box } from '@mui/material';
import { Tag } from 'lucide-react';
import { ElementType, useState } from 'react';
import { useMediaApplicationContext } from '../../../components/MediaApplicationContext';

const THUMBNAIL_SIZE = 24;
const THUMBNAIL_ASPECT_RATIO = '1 / 1';
const THUMBNAIL_FALLBACK_ICON_SIZE: IconSize = 'md';

interface ThumbnailProps {
  imageUrl?: string;
  alt: string;
  size?: number;
  aspectRatio?: string;
  fallbackIcon?: ElementType;
  fallbackIconSize?: IconSize;
}

const Thumbnail = ({
  imageUrl,
  alt,
  size = THUMBNAIL_SIZE,
  aspectRatio = THUMBNAIL_ASPECT_RATIO,
  fallbackIcon = Tag,
  fallbackIconSize = THUMBNAIL_FALLBACK_ICON_SIZE,
}: ThumbnailProps) => {
  const [failedImageUrl, setFailedImageUrl] = useState<string | undefined>(undefined);
  const mediaContext = useMediaApplicationContext();

  const resolvedImageUrl = imageUrl ? mediaContext.getMediaFromSize(imageUrl, 'xs') : undefined;
  const shouldShowImage = Boolean(resolvedImageUrl) && resolvedImageUrl !== failedImageUrl;

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: size,
        aspectRatio,
        flexShrink: 0,
        overflow: 'hidden',
        borderRadius: `${theme.shape.borderRadiusSm}px`,
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.secondary,
      })}
    >
      {shouldShowImage ? (
        <Box
          component="img"
          src={resolvedImageUrl}
          alt={alt}
          loading="lazy"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setFailedImageUrl(resolvedImageUrl)}
        />
      ) : (
        <Icon icon={fallbackIcon} size={fallbackIconSize} />
      )}
    </Box>
  );
};

export default Thumbnail;
